import { useEffect, useMemo, useRef, useState } from "react";
import { useTransition } from "@react-spring/web";
import { GetMerkActivitiesParams, MerkActivity } from "@letsbit/malcolmlb";
import { useGetMerkActivities } from "hooks/merk/useGetMerkActivities";
import { useIntroducingBrokerMemberMerkActivities } from "hooks/partnerApi/useIntroducingBrokerMemberMerkActivities";

export function useAlpacaActivitiesScroll<T extends HTMLElement>(
  historyCardHeight: number,
  params?: GetMerkActivitiesParams,
  firstPageOnly?: boolean,
  memberUid?: string,
) {
  const listContainerRef = useRef<T | null>(null);
  const [limit, setLimit] = useState<number | null>(null);
  const refMap = useMemo(() => new WeakMap(), []);
  // const infScrollRef = React.useRef<HTMLIonInfiniteScrollElement | null>(null);
  const config = {
    tension: 125,
    friction: 20,
    precision: 0.0001,
    // duration: 350,
  };

  useEffect(() => {
    if (
      listContainerRef.current &&
      listContainerRef.current.clientHeight &&
      listContainerRef.current.clientHeight > 0 &&
      !limit
    ) {
      const ratio = listContainerRef.current.clientHeight / historyCardHeight;

      setLimit(firstPageOnly ? Math.floor(ratio) - 2 : Math.floor(ratio + 2));
    }
  }, [listContainerRef, limit, historyCardHeight, firstPageOnly]);

  const { data: dataExp, ...swr } = memberUid
    ? useIntroducingBrokerMemberMerkActivities({
        ...params,
        page_token: params?.page_token,
        member_uid: memberUid,
      })
    : useGetMerkActivities({
        ...params,
        page_token: params?.page_token,
      });

  const data = dataExp?.flat() || [];

  const itemsTransitions = useTransition(data, {
    from: { opacity: 0, x: 50, height: historyCardHeight + 8 },
    keys: (item) => item.id,
    enter: () => async (next) => {
      await next({
        opacity: 1,
        height: historyCardHeight + 8,
        x: 0,
      });
    },
    leave: [{ x: 50, opacity: 0 }, { height: 0 }],
    config,
  });

  const forwardedRef = (item: MerkActivity) => (ref: HTMLDivElement) => {
    ref && refMap.set(item, ref);
  };

  return {
    listContainerRef,
    itemsTransitions,
    forwardedRef,
    hasMore: !dataExp || dataExp[dataExp.length - 1].length > 0,
    data,
    ...swr,
  };
}
